import React from "react";
import {
  Box,
  Link,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import logo from "../../../asset/TIE_LOGO.png";
import { useLocation, useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { WEB_URL } from "src/config";

// Centralized Nav Config
const navConfig = {
  default: [
    {
      title: "Dashboard",
      icon: <Icon icon="tdesign:dashboard-1" />,
      path: "home",
    },
    {
      title: "Support",
      icon: <Icon icon="ic:baseline-contact-phone" />,
      path: "support",
    },
    { title: "Order", icon: <Icon icon="ion:cart-outline" />, path: "order" },
    {
      title: "Community",
      icon: <Icon icon="heroicons-solid:user-group" />,
      path: "community",
    },
  ],
  studyAbroad: [
    {
      title: "Dashboard",
      icon: <Icon icon="tdesign:dashboard-1" />,
      path: "home",
    },
    {
      title: "Study Abroad Home",
      icon: <Icon icon="ion:home" />,
      path: "admission",
    },
    {
      title: "Document Management",
      icon: <Icon icon="mdi:file-document" />,
      path: "documentsverification",
    },
    {
      title: "Resource",
      icon: <Icon icon="grommet-icons:resources" />,
      path: "resources",
    },
    {
      title: "Support",
      icon: <Icon icon="ic:baseline-contact-phone" />,
      path: "support",
    },
    {
      title: "Order",
      icon: <Icon icon="ion:cart-outline" />,
      path: "order",
    },
    {
      title: "Community",
      icon: <Icon icon="heroicons-solid:user-group" />,
      path: "community",
    },
  ],
  testPrep: [
    {
      title: "Dashboard",
      icon: <Icon icon="tdesign:dashboard-1" />,
      path: "home",
    },
    {
      title: "Test Prep Home",
      icon: <Icon icon="ion:home" />,
      path: "test-prep",
    },

    {
      title: "Support",
      icon: <Icon icon="ic:baseline-contact-phone" />,
      path: "support",
    },
    {
      title: "Order",
      icon: <Icon icon="ion:cart-outline" />,
      path: "order",
    },
    {
      title: "Community",
      icon: <Icon icon="heroicons-solid:user-group" />,
      path: "community",
    },
  ],
  userBoard: [
    {
      title: "Dashboard",
      icon: <Icon icon="tdesign:dashboard-1" />,
      path: "home",
    },
    {
      title: "Test Prep Home",
      icon: <Icon icon="ion:home" />,
      path: "test-prep",
    },
    {
      title: "User Board",
      icon: <Icon icon="mingcute:black-board-2-line" />,
      path: "user-board",
    },
    {
      title: "Planner",
      icon: <Icon icon="streamline:class-lesson" />,
      path: "planner",
    },
    {
      title: "Learning Resources",
      icon: <Icon icon="grommet-icons:resources" />,
      path: "learningresources",
    },
    {
      title: "Test",
      icon: <Icon icon="heroicons:light-bulb" />,
      path: "mocktest",
    },
    {
      title: "Performance",
      icon: <Icon icon="game-icons:progression" />,
      path: "performance",
    },
    {
      title: "Support",
      icon: <Icon icon="ic:baseline-contact-phone" />,
      path: "support",
    },
    {
      title: "Order",
      icon: <Icon icon="ion:cart-outline" />,
      path: "order",
    },
    {
      title: "Community",
      icon: <Icon icon="heroicons-solid:user-group" />,
      path: "community",
    },
  ],
};

const NavBar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine which nav config to use
  let navItems = navConfig.default;
  if (
    location.pathname.startsWith("/dashboard/admission") ||
    location.pathname.startsWith("/dashboard/studyabroad") ||
    location.pathname.startsWith("/dashboard/documentsverification") ||
    location.pathname.startsWith("/dashboard/resources")
  ) {
    navItems = navConfig.studyAbroad;
  }
  if (
    location.pathname.startsWith("/dashboard/user-board") ||
    location.pathname.startsWith("/dashboard/mocktest") ||
    location.pathname.startsWith("/dashboard/planner") ||
    location.pathname.startsWith("/dashboard/learningresources") ||
    location.pathname.startsWith("/dashboard/performance")
  ) {
    navItems = navConfig.userBoard;
  } else if (location.pathname.startsWith("/dashboard/test-prep")) {
    navItems = navConfig.testPrep;
  }

  const handleLogoClick = () => {
    sessionStorage.removeItem("courseId");
    sessionStorage.removeItem("courseName");
    sessionStorage.removeItem("readMoreClicked");
    navigate("/dashboard/home");
  };

  return (
    <Box
      sx={{
        display: { md: "block", xs: "none", height: "100vh" },
      }}
    >
      <Box
        sx={{
          height: "100%",
          backgroundColor: "#fff",
          padding: "10px 5px",
          borderRight: "1px solid #ccc",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Logo */}
        <Link
          component={RouterLink}
          to={WEB_URL}
          sx={{ width: "220px", cursor: "pointer" }}
          onClick={handleLogoClick}
        >
          <img src={logo} alt="dashboard-logo" width={"100%"} />
        </Link>

        {/* Navigation List */}
        <List sx={{ marginTop: "20px" }}>
          {navItems.map((item, index) => {
            const fullPath = `/dashboard/${item.path}`;

            // Check if this path exactly matches or is a parent of the current location
            const isExactMatch = location.pathname === fullPath;
            const isParentMatch =
              location.pathname.startsWith(fullPath + "/") &&
              !navItems.some((i) => {
                const iFull = `/dashboard/${i.path}`;
                return (
                  iFull !== fullPath &&
                  location.pathname.startsWith(iFull + "/")
                );
              });

            const isSelected = isExactMatch || isParentMatch;

            return (
              <ListItem
                key={index}
                sx={{
                  margin: "5px 0",
                  padding: "8px 10px",
                  fontWeight: "500",
                  backgroundColor: isSelected ? "#C9EFFB" : "transparent",
                  color: isSelected ? "#000" : "#706C6D",
                  "&:hover": { backgroundColor: "#C9EFFB", color: "#000" },
                  cursor: "pointer",
                  transition: "backgroundColor 0.3s linear",
                  borderRadius: "5px",
                }}
                onClick={() => navigate(fullPath)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "auto",
                    marginRight: "8px",
                    fontSize: "18px",
                    color: isSelected ? "#000" : "#706C6D",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <Typography
                  variant="inherit"
                  sx={{
                    fontSize: "14px",
                    color: isSelected ? "#000" : "#706C6D",
                  }}
                >
                  {item.title}
                </Typography>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default NavBar;
